import React from 'react';
import {
  Col,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Row,
  TabContent,
  TabPane,
} from 'reactstrap';
import { toast } from 'react-toastify';
import { showSwal } from '../../../constants/swalConfig';

import ComponentCard from '../../../components/ComponentCard';
import BreadCrumbs from '../../../layouts/breadcrumbs/BreadCrumbs';
import api from '../../../constants/api';
import DataTable from '../../../components/common/DataTable';
import AppButton from '../../../components/common/AppButton';
import ActionButton from '../../../components/common/ActionButton';
import StatusBadge from '../../../components/common/StatusBadge';

/*
 * Roles & Countries — lookup tables behind the Employee form dropdowns.
 *
 * Both were previously hardcoded in the technician form and inserted straight
 * into the database by hand. driver_helper stores the VALUE (role_key /
 * country_name), not an id, so deletes are soft (is_active = 0): removing a
 * row outright would leave existing technicians pointing at something that is
 * no longer offered.
 */

const emptyRole = { role_key: '', role_name: '', is_active: 1 };
const emptyCountry = { country_code: '', country_name: '', dial_code: '', is_active: 1 };

export default function RolesCountries() {
  const [activeTab, setActiveTab] = React.useState('1');
  const [roles, setRoles] = React.useState([]);
  const [countries, setCountries] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [roleModal, setRoleModal] = React.useState(false);
  const [countryModal, setCountryModal] = React.useState(false);
  const [roleForm, setRoleForm] = React.useState(emptyRole);
  const [countryForm, setCountryForm] = React.useState(emptyCountry);

  const list = (res) => (Array.isArray(res?.data?.data) ? res.data.data : []);

  const load = React.useCallback(() => {
    setLoading(true);
    return Promise.all([
      api.get('/lookup/roles', { params: { activeOnly: false } }).then((r) => setRoles(list(r))),
      api
        .get('/lookup/countries', { params: { activeOnly: false } })
        .then((r) => setCountries(list(r))),
    ])
      .catch(() => toast.error('Unable to load roles and countries.'))
      .finally(() => setLoading(false));
  }, []);

  React.useEffect(() => {
    load();
  }, [load]);

  const openRole = (row) => {
    setRoleForm(row ? { ...row } : emptyRole);
    setRoleModal(true);
  };
  const openCountry = (row) => {
    setCountryForm(row ? { ...row } : emptyCountry);
    setCountryModal(true);
  };

  const saveRole = () => {
    if (!roleForm.role_key.trim() || !roleForm.role_name.trim()) {
      toast.error('Role key and name are both required.');
      return;
    }
    setSaving(true);
    const payload = {
      role_key: roleForm.role_key.trim().toLowerCase(),
      role_name: roleForm.role_name.trim(),
      is_active: Number(roleForm.is_active) ? 1 : 0,
    };
    const request = roleForm.id
      ? api.put(`/lookup/roles/${roleForm.id}`, payload)
      : api.post('/lookup/roles', payload);
    request
      .then(() => {
        toast.success(roleForm.id ? 'Role updated.' : 'Role added.');
        setRoleModal(false);
        return load();
      })
      .catch((err) => toast.error(err?.response?.data?.error || 'Unable to save role.'))
      .finally(() => setSaving(false));
  };

  const saveCountry = () => {
    if (!countryForm.country_code.trim() || !countryForm.country_name.trim()) {
      toast.error('Country code and name are both required.');
      return;
    }
    setSaving(true);
    const payload = {
      country_code: countryForm.country_code.trim().toUpperCase(),
      country_name: countryForm.country_name.trim(),
      dial_code: countryForm.dial_code || null,
      is_active: Number(countryForm.is_active) ? 1 : 0,
    };
    const request = countryForm.id
      ? api.put(`/lookup/countries/${countryForm.id}`, payload)
      : api.post('/lookup/countries', payload);
    request
      .then(() => {
        toast.success(countryForm.id ? 'Country updated.' : 'Country added.');
        setCountryModal(false);
        return load();
      })
      .catch((err) => toast.error(err?.response?.data?.error || 'Unable to save country.'))
      .finally(() => setSaving(false));
  };

  const deactivate = (kind, row) => {
    const label = kind === 'roles' ? row.role_name : row.country_name;
    showSwal({
  title: `Deactivate ${label}?`,
  text: 'It stays in the database and on existing records, but is hidden from dropdowns.',
  icon: 'warning',
  showCancelButton: true,
  confirmButtonText: 'Yes, deactivate',
}).then((result) => {
      if (!result.isConfirmed) return;
      api
        .delete(`/lookup/${kind}/${row.id}`)
        .then(() => {
          toast.success('Deactivated.');
          return load();
        })
        .catch(() => toast.error('Unable to deactivate.'));
    });
  };

  const statusCol = {
    key: 'is_active',
    header: 'Status',
    render: (row) =>
      Number(row.is_active) ? (
        <StatusBadge variant="success" label="Active" />
      ) : (
        <StatusBadge variant="danger" label="InActive" />
      ),
  };

  const roleColumns = [
    { key: 'sno', header: 'S.No', render: (row, i) => i + 1 },
    { key: 'role_name', header: 'Role Name', sortable: true },
    { key: 'role_key', header: 'Stored Value', sortable: true },
    statusCol,
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <div className="d-flex gap-2">
          <ActionButton action="edit" onClick={() => openRole(row)} />
          <ActionButton
            action="delete"
            title="Deactivate"
            onClick={() => deactivate('roles', row)}
          />
        </div>
      ),
    },
  ];

  const countryColumns = [
    { key: 'sno', header: 'S.No', render: (row, i) => i + 1 },
    { key: 'country_name', header: 'Country', sortable: true },
    { key: 'country_code', header: 'Code', sortable: true },
    { key: 'dial_code', header: 'Dial Code' },
    statusCol,
    {
      key: 'action',
      header: 'Action',
      render: (row) => (
        <div className="d-flex gap-2">
          <ActionButton action="edit" onClick={() => openCountry(row)} />
          <ActionButton
            action="delete"
            title="Deactivate"
            onClick={() => deactivate('countries', row)}
          />
        </div>
      ),
    },
  ];

  return (
    <>
      <BreadCrumbs />
      <div className="roles-countries-page">
        <Row>
          <Col sm="12">
            <ComponentCard title="Roles & Countries">
            <Nav tabs className="mb-3">
              <NavItem>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={activeTab === '1' ? 'active' : ''}
                  onClick={() => setActiveTab('1')}
                >
                  Roles
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  style={{ cursor: 'pointer' }}
                  className={activeTab === '2' ? 'active' : ''}
                  onClick={() => setActiveTab('2')}
                >
                  Countries
                </NavLink>
              </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
              <TabPane tabId="1">
                <DataTable
                  columns={roleColumns}
                  mode="client"
                  data={loading ? [] : roles}
                  rowKey={(row) => row.id}
                  searchPlaceholder="Search roles..."
                  toolbar={
                    <AppButton
                      label="+ Add Role"
                      variant="buttonColor"
                      onClick={() => openRole(null)}
                    />
                  }
                  emptyMessage={loading ? 'Loading…' : 'No roles yet'}
                />
              </TabPane>

              <TabPane tabId="2">
                <DataTable
                  columns={countryColumns}
                  mode="client"
                  data={loading ? [] : countries}
                  rowKey={(row) => row.id}
                  searchPlaceholder="Search countries..."
                  toolbar={
                    <AppButton
                      label="+ Add Country"
                      variant="buttonColor"
                      onClick={() => openCountry(null)}
                    />
                  }
                  emptyMessage={loading ? 'Loading…' : 'No countries yet'}
                />
              </TabPane>
            </TabContent>
            </ComponentCard>
          </Col>
        </Row>
      </div>

      <Modal className="roles-countries-modal roles-countries-role-modal" isOpen={roleModal} toggle={() => setRoleModal(false)}>
        <ModalHeader toggle={() => setRoleModal(false)}>
          {roleForm.id ? 'Edit Role' : 'Add Role'}
        </ModalHeader>
        <ModalBody>
          <div className="bk-field">
            <Label for="role-name">
              Role Name<span className="req">*</span>
            </Label>
            <Input
              id="role-name"
              className="form-control"
              value={roleForm.role_name}
              placeholder="Driver"
              onChange={(e) => setRoleForm({ ...roleForm, role_name: e.target.value })}
            />
          </div>
          <div className="bk-field">
            <Label for="role-key">
              Stored Value<span className="req">*</span>
            </Label>
            <Input
              id="role-key"
              className="form-control"
              value={roleForm.role_key}
              placeholder="driver"
              onChange={(e) => setRoleForm({ ...roleForm, role_key: e.target.value })}
            />
            <small className="text-muted">Lowercase, no spaces. Must be unique.</small>
          </div>
          <div className="bk-field role-active-field">
            <Label className="d-inline-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={Number(roleForm.is_active) === 1}
                onChange={(e) => setRoleForm({ ...roleForm, is_active: e.target.checked ? 1 : 0 })}
              />
              Active (shown in dropdowns)
            </Label>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <AppButton
              label={saving ? 'Saving...' : 'Save Role'}
              variant="buttonColor"
              disabled={saving}
              onClick={saveRole}
            />
            <AppButton label="Cancel" variant="buttonColor" outline onClick={() => setRoleModal(false)} />
          </div>
        </ModalBody>
      </Modal>

      <Modal className="roles-countries-modal roles-countries-country-modal" isOpen={countryModal} toggle={() => setCountryModal(false)}>
        <ModalHeader toggle={() => setCountryModal(false)}>
          {countryForm.id ? 'Edit Country' : 'Add Country'}
        </ModalHeader>
        <ModalBody>
          <div className="bk-field">
            <Label for="country-name">
              Country Name<span className="req">*</span>
            </Label>
            <Input
              id="country-name"
              className="form-control"
              value={countryForm.country_name}
              placeholder="India"
              onChange={(e) => setCountryForm({ ...countryForm, country_name: e.target.value })}
            />
          </div>
          <div className="bk-field">
            <Label for="country-code">
              ISO Code<span className="req">*</span>
            </Label>
            <Input
              id="country-code"
              className="form-control"
              maxLength={2}
              value={countryForm.country_code}
              placeholder="IN"
              onChange={(e) => setCountryForm({ ...countryForm, country_code: e.target.value })}
            />
          </div>
          <div className="bk-field">
            <Label for="country-dial">Dial Code</Label>
            <Input
              id="country-dial"
              className="form-control"
              value={countryForm.dial_code || ''}
              placeholder="+91"
              onChange={(e) => setCountryForm({ ...countryForm, dial_code: e.target.value })}
            />
          </div>
          <div className="bk-field country-active-field">
            <Label className="d-inline-flex align-items-center gap-2">
              <input
                type="checkbox"
                checked={Number(countryForm.is_active) === 1}
                onChange={(e) =>
                  setCountryForm({ ...countryForm, is_active: e.target.checked ? 1 : 0 })
                }
              />
              Active (shown in dropdowns)
            </Label>
          </div>
          <div className="d-flex justify-content-end gap-2">
            <AppButton
              label={saving ? 'Saving...' : 'Save Country'}
              variant="buttonColor"
              disabled={saving}
              onClick={saveCountry}
            />
            <AppButton
              label="Cancel"
              variant="buttonColor"
              outline
              onClick={() => setCountryModal(false)}
            />
          </div>
        </ModalBody>
      </Modal>
    </>
  );
}
